import { useState } from 'react';
import { formatCurrency, formatPercent } from '../utils/calculations';
import { GOOGLE_API_KEY } from '../utils/googleMaps';

async function toDataURL(src) {
  try {
    const url = src?.includes('zillowstatic.com')
      ? `/api/property/image-proxy?url=${encodeURIComponent(src)}`
      : src;
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror  = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export default function PDFReport({ dealData }) {
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    if (!dealData) return;
    setGenerating(true);

    try {
      const { default: jsPDF }     = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const L = 15, R = pageW - 15, mid = pageW / 2;

      const INK    = [15,  23,  42];
      const STEEL  = [30,  41,  59];
      const ACCENT = [37,  99, 235];
      const TEAL   = [13, 148, 136];
      const LIGHT  = [248, 250, 252];
      const MUTED  = [100, 116, 139];
      const WHITE  = [255, 255, 255];
      const GREEN  = [22, 163, 74];
      const RED    = [220, 38, 38];

      const strategyLabels = { flip:'Fix & Flip', hold:'Buy & Hold', brrrr:'BRRRR', wholesale:'Wholesale', airbnb:'Airbnb / STR' };
      const strategy  = dealData.strategy || 'flip';
      const dateStr   = new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' });

      let imgData = dealData.imageUrl ? await toDataURL(dealData.imageUrl) : null;
      if (!imgData && dealData.address) {
        const svUrl = `https://maps.googleapis.com/maps/api/streetview?size=600x300&location=${encodeURIComponent(dealData.address)}&key=${GOOGLE_API_KEY}&fov=90&pitch=5&source=outdoor`;
        imgData = await toDataURL(svUrl);
      }

      // ── Header ───────────────────────────────────────────────
      const PHOTO_W = imgData ? 68 : 0;
      const HDR_H   = 42;

      doc.setFillColor(...INK);
      doc.rect(0, 0, imgData ? pageW - PHOTO_W : pageW, HDR_H, 'F');
      doc.setFillColor(...ACCENT);
      doc.rect(0, 0, 5, HDR_H, 'F');

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...WHITE);
      doc.text('DEAL ANALYSIS REPORT', L + 6, 16);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(180, 190, 210);
      doc.text(`Strategy: ${strategyLabels[strategy] || strategy}`, L + 6, 26);
      doc.text(`Generated: ${dateStr}`, L + 6, 33);

      if (imgData) {
        doc.addImage(imgData, 'JPEG', pageW - PHOTO_W, 0, PHOTO_W, HDR_H, undefined, 'FAST');
        doc.setDrawColor(...ACCENT);
        doc.setLineWidth(0.8);
        doc.rect(pageW - PHOTO_W, 0, PHOTO_W, HDR_H);
      }

      let y = HDR_H + 8;

      // ── Address band ─────────────────────────────────────────
      doc.setFillColor(...LIGHT);
      doc.rect(0, y - 4, pageW, 20, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...INK);
      const addrLines = doc.splitTextToSize(dealData.address || 'Property Address', pageW - L * 2 - 10);
      doc.text(addrLines, L, y + 4);

      const meta = [
        dealData.beds      && `${dealData.beds} Beds`,
        dealData.baths     && `${dealData.baths} Baths`,
        dealData.sqft      && `${Number(dealData.sqft).toLocaleString()} sqft`,
        dealData.yearBuilt && `Built ${dealData.yearBuilt}`,
        dealData.propertyType,
      ].filter(Boolean).join('  ·  ');

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MUTED);
      doc.text(meta, L, y + 12);
      y += 26;

      // ── Helpers ──────────────────────────────────────────────
      const sectionHeader = (title, yPos, color = ACCENT) => {
        doc.setFillColor(...color);
        doc.rect(L, yPos, 3, 7, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...color);
        doc.text(title, L + 7, yPos + 5.5);
        doc.setDrawColor(...color);
        doc.setLineWidth(0.2);
        doc.line(L + 7, yPos + 7, R, yPos + 7);
        return yPos + 13;
      };

      const addFooter = () => {
        const fy = pageH - 10;
        doc.setFillColor(...INK);
        doc.rect(0, fy - 5, pageW, 15, 'F');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 165, 185);
        doc.text('For informational purposes only. Consult a licensed real estate professional before making investment decisions.', mid, fy + 1, { align: 'center' });
        doc.text(`ProPARV  ·  ${dateStr}`, mid, fy + 5, { align: 'center' });
      };

      const checkPage = (neededY) => {
        if (neededY > pageH - 25) {
          addFooter();
          doc.addPage();
          doc.setFillColor(...INK);
          doc.rect(0, 0, pageW, 14, 'F');
          doc.setFillColor(...ACCENT);
          doc.rect(0, 0, 5, 14, 'F');
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...WHITE);
          doc.text('DEAL ANALYSIS REPORT', L + 3, 9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(150, 165, 185);
          doc.text((dealData.address || '').split(',')[0], R, 9, { align: 'right' });
          return 22;
        }
        return neededY;
      };

      // ── KEY METRICS HIGHLIGHT CARDS ───────────────────────────
      y = sectionHeader('KEY METRICS', y);

      const profit = dealData.profit || 0;
      const roi    = dealData.roi    || 0;

      const cards = [
        { label: 'ARV',           value: formatCurrency(dealData.arv),           color: ACCENT },
        { label: 'Purchase Price',value: formatCurrency(dealData.purchasePrice),  color: STEEL  },
        { label: 'Rehab Cost',    value: formatCurrency(dealData.rehabCost),      color: [234,179,8] },
        { label: 'Est. Profit',   value: formatCurrency(profit),                  color: profit >= 0 ? GREEN : RED },
        { label: 'ROI',           value: formatPercent(roi),                      color: roi >= 15 ? GREEN : RED },
        { label: 'Cash Flow/mo',  value: formatCurrency(dealData.cashFlow),       color: (dealData.cashFlow||0) >= 0 ? GREEN : RED },
      ];

      const cardW = (pageW - L * 2 - 10) / 3;
      const cardH = 22;
      cards.forEach((c, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const cx  = L + col * (cardW + 5);
        const cy  = y + row * (cardH + 6);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(cx, cy, cardW, cardH, 2, 2, 'F');
        doc.setDrawColor(...c.color);
        doc.setLineWidth(0.6);
        doc.line(cx + 2, cy + 3, cx + 2, cy + cardH - 3);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...MUTED);
        doc.text(c.label, cx + 8, cy + 8);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...c.color);
        doc.text(c.value, cx + 8, cy + 17);
      });

      y += 2 * (cardH + 6) + 6;

      // ── INVESTMENT ANALYSIS TABLE ─────────────────────────────
      y = checkPage(y + 10);
      y = sectionHeader('INVESTMENT BREAKDOWN', y, TEAL);

      const allIn = (dealData.purchasePrice || 0) + (dealData.rehabCost || 0);

      autoTable(doc, {
        startY: y,
        head: [['Metric', 'Value', 'Metric', 'Value']],
        body: [
          ['Purchase Price',      formatCurrency(dealData.purchasePrice),    'After-Repair Value (ARV)', formatCurrency(dealData.arv)],
          ['Rehab / Repair Cost', formatCurrency(dealData.rehabCost),        'All-In Cost',              formatCurrency(allIn)],
          ['Down Payment',        formatCurrency(dealData.downPayment),       'Loan Amount',              formatCurrency((dealData.purchasePrice||0)-(dealData.downPayment||0))],
          ['Holding Costs',       formatCurrency(dealData.holdingCosts),      'Selling Costs (6%)',       formatCurrency(dealData.sellingCosts)],
          ['Total Investment',    formatCurrency(dealData.totalInvestment),   'Gross Profit',             formatCurrency(dealData.grossProfit)],
          ['Net Profit',          formatCurrency(dealData.profit),            'ROI',                      formatPercent(dealData.roi)],
          ['Monthly Mortgage',    formatCurrency(dealData.monthlyMortgage),   'Monthly Cash Flow',        formatCurrency(dealData.cashFlow)],
          ['Cap Rate',            formatPercent(dealData.capRate),            'Max Allowable Offer',      formatCurrency(dealData.mao)],
        ],
        styles:      { fontSize: 8.5, cellPadding: [4.5, 6], textColor: STEEL },
        headStyles:  { fillColor: STEEL, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
        columnStyles: {
          0: { fontStyle: 'normal', textColor: MUTED, cellWidth: 46 },
          1: { fontStyle: 'bold',   textColor: INK,   cellWidth: 42 },
          2: { fontStyle: 'normal', textColor: MUTED, cellWidth: 46 },
          3: { fontStyle: 'bold',   textColor: INK },
        },
        alternateRowStyles: { fillColor: [249, 251, 253] },
        margin: { left: L, right: L },
        tableLineColor: [220, 228, 240],
        tableLineWidth: 0.2,
      });
      y = doc.lastAutoTable.finalY + 12;

      // ── COMPARABLE SALES ─────────────────────────────────────
      const comps = (dealData.comps || []).filter(c => c.price);
      if (comps.length > 0) {
        y = checkPage(y + 10);
        y = sectionHeader('COMPARABLE SALES (COMPS)', y, ACCENT);

        autoTable(doc, {
          startY: y,
          head: [['Address', 'Sale Price', 'Beds', 'Baths', 'Sqft', '$/sqft', 'Date']],
          body: comps.map(c => [
            c.address ? c.address.split(',')[0] : '—',
            formatCurrency(c.price),
            c.beds  || '—',
            c.baths || '—',
            c.sqft  ? Number(c.sqft).toLocaleString() : '—',
            c.sqft && c.price ? formatCurrency(Math.round(c.price / c.sqft)) : '—',
            c.date  || '—',
          ]),
          styles:      { fontSize: 8, cellPadding: [4, 5], textColor: STEEL },
          headStyles:  { fillColor: ACCENT, textColor: WHITE, fontStyle: 'bold', fontSize: 7.5 },
          alternateRowStyles: { fillColor: [249, 251, 253] },
          margin: { left: L, right: L },
          tableLineColor: [220, 228, 240],
          tableLineWidth: 0.2,
        });
        y = doc.lastAutoTable.finalY + 12;
      }

      // ── AI INSIGHT ───────────────────────────────────────────
      if (dealData.aiInsight) {
        y = checkPage(y + 10);
        y = sectionHeader('AI INVESTMENT INSIGHT', y, [99, 102, 241]);

        doc.setFillColor(245, 245, 255);
        const insightLines = doc.splitTextToSize(dealData.aiInsight, pageW - L * 2 - 16);
        const boxH = insightLines.length * 5.2 + 14;
        y = checkPage(y + boxH);
        doc.roundedRect(L, y, pageW - L * 2, boxH, 2, 2, 'F');
        doc.setFontSize(8.2);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...STEEL);
        doc.text(insightLines, L + 8, y + 9);
        y += boxH + 10;
      }

      addFooter();

      const filename = `ProPARV-Analysis-${(dealData.address || 'property').split(',')[0].replace(/[^a-z0-9]/gi,'-').replace(/-+/g,'-').toLowerCase().slice(0,35)}.pdf`;
      doc.save(filename);

    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('PDF generation failed: ' + err.message);
    }

    setGenerating(false);
  };

  return (
    <button
      onClick={generatePDF}
      disabled={generating || !dealData}
      className="btn btn-primary"
      style={{ padding: '8px 18px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}
    >
      {generating
        ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Generating...</>
        : <>📊 Export Analysis</>}
    </button>
  );
}
